import { Transition } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import DropIcon from 'jsx:../../../assets/drop-icon.svg';
import FileIcon from 'jsx:../../../assets/file-icon.svg';
import { useMemo } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as z from 'zod';
import { useAttachmentDelete } from '../../api/use-attachment-delete';
import { useAttachmentUpload } from '../../api/use-attachment-upload';
import { ProgressLayout } from '../../components/layouts/progress-layout';
import { ManualSection } from '../../components/manual-section';
import { PatchInviteForm } from '../../components/patch-invite-form';
import { Button } from '../../components/ui/button';
import { ErrorBlock } from '../../components/ui/error-block';
import { InfoBlock } from '../../components/ui/info-block';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { TrashCard } from '../../components/ui/trash-card';
import { useInviteStore } from '../../store/use-invite-store';

const manualSchema = z.object({
  name: z.string().min(1),
  dose: z.string().min(1),
  frequency: z.string().min(1),
});

export const schema = z.object({
  medicationsConfirmed: z.literal(true),
  medicationsManual: z.array(manualSchema).default([]),
});

function extractDefaults({ medicationsManual }) {
  return {
    medicationsConfirmed: true,
    medicationsManual: medicationsManual ?? [],
  };
}

function transformValues({ medicationsManual }) {
  return {
    medicationsConfirmed: true,
    medicationsManual,
  };
}

const fileTypes = ['PNG', 'JPG', 'GIF', 'PDF'];
const fileExtensions = fileTypes.slice(0, -1).join(', ');
const fileExtension = fileTypes.at(-1);
const fileMaxSize = 10;

export function Medications(props) {
  const { t } = useTranslation('page.medications');

  return (
    <PatchInviteForm
      nextPath={props.next.path}
      extractDefaults={extractDefaults}
      transformValues={transformValues}
      schema={schema}
      markVisited={props.currentStepKey}
    >
      <ProgressLayout {...props}>
        <h1 className="title">{t('title')}</h1>
        <Trans
          i18nKey="text"
          ns="page.medications"
          components={{
            p: <p />,
          }}
        />

        <InfoBlock className="!pt-2.5">
          <Trans
            i18nKey="uploadText"
            ns="page.medications"
            components={{
              p: <p className="mb-2.5 last:mb-0" />,
              strong: <strong className="block mb-2.5 font-semibold" />,
            }}
          />
          <UploadAttachment />
          <InviteAttachments />
        </InfoBlock>

        <InfoBlock className="!pt-2.5">
          <Trans
            i18nKey="manuallyText"
            ns="page.medications"
            components={{
              p: <p className="mb-2.5 last:mb-0" />,
              strong: <strong className="block mb-2.5 font-semibold" />,
            }}
          />
          <ManualSection
            fieldArrayName="medicationsManual"
            ItemComponent={ManualMedication}
            showFromButtonLabel={t('addButtonLabel')}
            FormComponent={ManualMedicationForm}
          />
        </InfoBlock>
      </ProgressLayout>
    </PatchInviteForm>
  );
}

function ManualMedication({ item }) {
  const { name, dose, frequency } = item;
  const { t } = useTranslation('page.medications');
  return (
    <div className="px-2.5 flex flex-col items-start">
      <h1 className="block mb-2 font-medium">{name}</h1>
      <p className="caption">
        <span>{dose}</span> • <span>{t(`select.${frequency}`)}</span>
      </p>
    </div>
  );
}

function ManualMedicationForm({ onAdd }) {
  const { t } = useTranslation('page.medications');
  const { append } = useFieldArray({ name: 'medicationsManual' });
  const form = useForm({
    defaultValues: { name: '', dose: '', frequency: '' },
    resolver: zodResolver(manualSchema),
  });
  const { isValid } = form.formState;
  const frequencyOptions = useMemo(
    () => [
      {
        value: 'once-daily',
        label: t('select.once-daily'),
      },
      {
        value: 'twice-daily',
        label: t('select.twice-daily'),
      },
      {
        value: 'three-time-daily',
        label: t('select.three-time-daily'),
      },
      {
        value: 'as-needed',
        label: t('select.as-needed'),
      },
    ],
    [t],
  );

  return (
    <FormProvider {...form}>
      <div className="space-y-4">
        <Input name="name" label={t('medicationName')} placeholder={t('medicationPlaceholder')} />
        <Input name="dose" label={t('dose')} placeholder={t('dosePlaceholder')} />
        <Select name="frequency" options={frequencyOptions} label={t('frequency')} />
        <Button
          type="button"
          variant="primary-light"
          onClick={form.handleSubmit((values) => {
            append(values);
            form.reset();
            onAdd();
          })}
          disabled={!isValid}
        >
          {t('saveButton')}
        </Button>
      </div>
    </FormProvider>
  );
}

function UploadAttachment() {
  const { t } = useTranslation('page.medications');
  const { error, execute } = useAttachmentUpload();

  return (
    <>
      <ErrorBlock error={error} />
      <FileUploader
        classes="group my-5 flex flex-col items-center p-5.5 pb-6.5 w-full border-2 border-dashed border-light-300 text-center rounded-md transition-colors hover:border-primary"
        //multiple
        types={fileTypes}
        handleChange={(file) => {
          execute(file);
        }}
        name="file"
        hoverTitle={t('dropHere')}
      >
        <DropIcon
          aria-hidden="true"
          className="mb-1 text-light-400 transition-colors group-hover:text-primary"
        />
        <Trans
          i18nKey="dropTitle"
          ns="page.medications"
          components={{
            p: <p className="mb-1 last:mb-0 font-medium text-light-600" />,
            strong: <span className="text-primary" />,
          }}
        />
        <Trans
          i18nKey="dropText"
          ns="page.medications"
          values={{ fileExtensions, fileExtension, mb: fileMaxSize }}
          components={{
            p: <p className="text-small font-normal" />,
          }}
        />
      </FileUploader>
    </>
  );
}

function InviteAttachments() {
  const { invite } = useInviteStore();

  const cards = useMemo(
    () =>
      invite.attachments.map((attachment) => (
        <Transition
          key={attachment.attachmentId}
          show={true}
          appear={true}
          enter="transition-opacity duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          as="li"
        >
          <InviteAttachmentCard attachment={attachment} />
        </Transition>
      )),
    [invite.attachments],
  );

  return (
    <ul className="relative z-1 py-2.5 space-y-2.5 empty:hidden before:absolute before:-z-1 before:top-0 before:left-[-99999px] before:right-[-99999px] before:bottom-0 before:bg-gray-100">
      {cards}
    </ul>
  );
}

function InviteAttachmentCard({ attachment }) {
  const { loading, execute } = useAttachmentDelete();
  return (
    <TrashCard trashCard disabled={loading} onClick={() => execute(attachment.attachmentId)}>
      <FileIcon className="text-light-500" />
      <strong className="font-medium text-base text-body-dark">{attachment.fileName}</strong>
    </TrashCard>
  );
}
